/**
 * Time Log Store
 * Manages time tracking state using Zustand
 */

import { create } from 'zustand';
import { TimeLog, TimeLogSummary, TimeLogReport } from '@/types';

interface TimeLogState {
  // State
  taskTimeLogs: Map<string, TimeLog[]>;
  myTimeLogs: TimeLog[];
  projectTimeLogs: TimeLog[];
  taskSummary: Map<string, TimeLogSummary>;
  mySummary: TimeLogSummary | null;
  projectSummary: TimeLogSummary | null;
  projectReport: TimeLogReport | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  setTaskTimeLogs: (taskId: string, logs: TimeLog[]) => void;
  addTimeLog: (log: TimeLog) => void;
  updateTimeLog: (log: TimeLog) => void;
  removeTimeLog: (timeLogId: string, taskId?: string) => void;
  setMyTimeLogs: (logs: TimeLog[]) => void;
  setProjectTimeLogs: (logs: TimeLog[]) => void;
  setTaskSummary: (taskId: string, summary: TimeLogSummary) => void;
  setMySummary: (summary: TimeLogSummary) => void;
  setProjectSummary: (summary: TimeLogSummary) => void;
  setProjectReport: (report: TimeLogReport) => void;
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  clearTimeLogs: () => void;
}

export const useTimeLogStore = create<TimeLogState>()((set) => ({
  // Initial state
  taskTimeLogs: new Map(),
  myTimeLogs: [],
  projectTimeLogs: [],
  taskSummary: new Map(),
  mySummary: null,
  projectSummary: null,
  projectReport: null,
  isLoading: false,
  error: null,

  // Actions
  setTaskTimeLogs: (taskId, logs) =>
    set((state) => {
      const newTaskTimeLogs = new Map(state.taskTimeLogs);
      newTaskTimeLogs.set(taskId, logs);
      return { taskTimeLogs: newTaskTimeLogs };
    }),

  addTimeLog: (log) =>
    set((state) => {
      const newTaskTimeLogs = new Map(state.taskTimeLogs);
      const currentLogs = newTaskTimeLogs.get(log.taskId) || [];
      newTaskTimeLogs.set(log.taskId, [log, ...currentLogs]);

      return {
        taskTimeLogs: newTaskTimeLogs,
        myTimeLogs: [log, ...state.myTimeLogs],
      };
    }),

  updateTimeLog: (log) =>
    set((state) => {
      const newTaskTimeLogs = new Map(state.taskTimeLogs);
      const currentLogs = newTaskTimeLogs.get(log.taskId) || [];
      newTaskTimeLogs.set(
        log.taskId,
        currentLogs.map((l) => (l.timeLogId === log.timeLogId ? log : l))
      );

      return {
        taskTimeLogs: newTaskTimeLogs,
        myTimeLogs: state.myTimeLogs.map((l) =>
          l.timeLogId === log.timeLogId ? log : l
        ),
        projectTimeLogs: state.projectTimeLogs.map((l) =>
          l.timeLogId === log.timeLogId ? log : l
        ),
      };
    }),

  removeTimeLog: (timeLogId, taskId) =>
    set((state) => {
      const newTaskTimeLogs = new Map(state.taskTimeLogs);

      if (taskId) {
        const currentLogs = newTaskTimeLogs.get(taskId) || [];
        newTaskTimeLogs.set(
          taskId,
          currentLogs.filter((l) => l.timeLogId !== timeLogId)
        );
      } else {
        // Remove from all task logs
        newTaskTimeLogs.forEach((logs, key) => {
          newTaskTimeLogs.set(
            key,
            logs.filter((l) => l.timeLogId !== timeLogId)
          );
        });
      }

      return {
        taskTimeLogs: newTaskTimeLogs,
        myTimeLogs: state.myTimeLogs.filter((l) => l.timeLogId !== timeLogId),
        projectTimeLogs: state.projectTimeLogs.filter(
          (l) => l.timeLogId !== timeLogId
        ),
      };
    }),

  setMyTimeLogs: (logs) => set({ myTimeLogs: logs }),

  setProjectTimeLogs: (logs) => set({ projectTimeLogs: logs }),

  setTaskSummary: (taskId, summary) =>
    set((state) => {
      const newTaskSummary = new Map(state.taskSummary);
      newTaskSummary.set(taskId, summary);
      return { taskSummary: newTaskSummary };
    }),

  setMySummary: (summary) => set({ mySummary: summary }),

  setProjectSummary: (summary) => set({ projectSummary: summary }),

  setProjectReport: (report) => set({ projectReport: report }),

  setIsLoading: (isLoading) => set({ isLoading }),

  setError: (error) => set({ error }),

  clearTimeLogs: () =>
    set({
      taskTimeLogs: new Map(),
      myTimeLogs: [],
      projectTimeLogs: [],
      taskSummary: new Map(),
      mySummary: null,
      projectSummary: null,
      projectReport: null,
      error: null,
    }),
}));

// Selectors
export const useTaskTimeLogs = (taskId: string) =>
  useTimeLogStore((state) => state.taskTimeLogs.get(taskId) || []);

export const useMyTimeLogs = () => useTimeLogStore((state) => state.myTimeLogs);

export const useProjectTimeLogs = () =>
  useTimeLogStore((state) => state.projectTimeLogs);

export const useTaskTimeLogSummary = (taskId: string) =>
  useTimeLogStore((state) => state.taskSummary.get(taskId));

export const useMyTimeLogSummary = () =>
  useTimeLogStore((state) => state.mySummary);

export const useProjectTimeLogSummary = () =>
  useTimeLogStore((state) => state.projectSummary);

export const useProjectTimeLogReport = () =>
  useTimeLogStore((state) => state.projectReport);

export const useTimeLogLoading = () =>
  useTimeLogStore((state) => state.isLoading);

export const useTimeLogError = () => useTimeLogStore((state) => state.error);

export default useTimeLogStore;
